import { ArrowLeft, Star } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { NocheLogo } from "@/components/NocheLogo";
import { BusinessMenu } from "@/components/BusinessMenu";

const FeatureShowcase = () => {
  const featureCategories = [
    {
      id: "guest-management",
      name: "Guest Management",
      description: "Tools for managing guest lists, VIPs, and attendees",
      features: [
        {
          title: "Smart Guest Lists",
          description: "Quickly add or remove guests, track RSVPs, and categorize guests (big spenders, influencers, etc.)",
          status: "Coming Soon"
        },
        {
          title: "VIP Management",
          description: "Automatic reminders to follow up with VIPs or high-value guests",
          status: "In Development"
        },
        {
          title: "Smart Search",
          description: "Find and invite specific categories of guests by average spend, arrival time, or influence factor",
          status: "Planned"
        }
      ]
    },
    {
      id: "promotions",
      name: "Promotions & Discounts",
      description: "Tools for creating and managing promotions",
      features: [
        {
          title: "Digital Drink Tickets",
          description: "Provide digital drink tickets or unique promo codes directly in the app",
          status: "Coming Soon"
        },
        {
          title: "Usage Tracking",
          description: "Track usage so you know how many freebies get redeemed",
          status: "In Development"
        },
        {
          title: "Dynamic Pricing",
          description: "Dynamic pricing or happy hour deals that can be pushed to specific user segments",
          status: "Planned"
        }
      ]
    },
    {
      id: "analytics",
      name: "Analytics & Insights",
      description: "Data-driven insights to optimize your venue",
      features: [
        {
          title: "Real-Time Dashboards",
          description: "Track ticket sales, expected turnout, current check-ins, bar revenue, and more",
          status: "Coming Soon"
        },
        {
          title: "Predictive Attendance",
          description: "Estimate attendance based on past data and real-time factors like weather",
          status: "In Development"
        },
        {
          title: "AI-Driven Tips",
          description: "Get AI-powered suggestions for boosting event performance",
          status: "Planned"
        }
      ]
    },
    {
      id: "marketing",
      name: "AI Marketing",
      description: "Intelligent marketing tools powered by AI",
      features: [
        {
          title: "Targeted Promotion",
          description: "Privacy-friendly targeted promotions filtered by demographic or behavior",
          status: "Coming Soon"
        },
        {
          title: "AI-Generated Content",
          description: "Automated, AI-generated ad copy and images for social media campaigns",
          status: "In Development"
        },
        {
          title: "A/B Testing",
          description: "Test different promotions to see which messaging pulls the biggest response",
          status: "Planned"
        }
      ]
    },
    {
      id: "social",
      name: "Social Media",
      description: "Integrate with social platforms for maximum reach",
      features: [
        {
          title: "Platform Integration",
          description: "Connect Instagram, Facebook, TikTok to run custom ad campaigns",
          status: "Coming Soon"
        },
        {
          title: "Automated Posting",
          description: "Schedule and automate posting directly from the app",
          status: "In Development"
        },
        {
          title: "Influencer Analytics",
          description: "Pull user social media data to gauge influencer reach",
          status: "Planned"
        }
      ]
    },
    {
      id: "collaboration",
      name: "Event Collaboration",
      description: "Role-specific tools for your entire team",
      features: [
        {
          title: "Role-Based Access",
          description: "Specific toolkits for DJs, bartenders, security, performers, and more",
          status: "Coming Soon"
        },
        {
          title: "Staff Management",
          description: "Manage staff schedules, roles, and permissions",
          status: "In Development"
        },
        {
          title: "Real-Time Coordination",
          description: "Coordinate last-minute changes and communicate with staff",
          status: "Planned"
        }
      ]
    },
    {
      id: "communication",
      name: "Communication",
      description: "Keep everyone in the loop",
      features: [
        {
          title: "In-App Chat",
          description: "Chat with staff in real time to coordinate changes",
          status: "Coming Soon"
        },
        {
          title: "Push Notifications",
          description: "Send notifications to guests about special deals or DJ sets",
          status: "In Development"
        },
        {
          title: "Automated Reminders",
          description: "Schedule automated reminders for upcoming events",
          status: "Planned"
        }
      ]
    },
    {
      id: "privacy",
      name: "Privacy & Data",
      description: "Responsible data handling and privacy controls",
      features: [
        {
          title: "Opt-In Data Collection",
          description: "Options for anonymous or opt-in only data collection",
          status: "Coming Soon"
        },
        {
          title: "Customizable Sharing",
          description: "Customizable data sharing settings for precise control",
          status: "In Development"
        },
        {
          title: "GDPR Compliance",
          description: "Full compliance with privacy regulations and best practices",
          status: "Planned"
        }
      ]
    },
    {
      id: "payments",
      name: "Revenue & Payments",
      description: "Manage finances and track revenue",
      features: [
        {
          title: "POS Integration",
          description: "Integrate with point-of-sale systems to track bar sales in real time",
          status: "Coming Soon"
        },
        {
          title: "Ticket Sales",
          description: "Sell tickets directly through the app with digital storage on phones",
          status: "In Development"
        },
        {
          title: "Financial Reporting",
          description: "Comprehensive financial reporting and analytics",
          status: "Planned"
        }
      ]
    },
    {
      id: "reporting",
      name: "Event Analysis",
      description: "Post-event insights and reporting",
      features: [
        {
          title: "Event Recap",
          description: "Quick recap of the event's performance including attendance and revenue",
          status: "Coming Soon"
        },
        {
          title: "Marketing Analysis",
          description: "Summarize marketing spend vs. results from campaigns",
          status: "In Development"
        },
        {
          title: "Improvement Suggestions",
          description: "AI-powered suggestions for improving future events",
          status: "Planned"
        }
      ]
    },
    {
      id: "patron-experience",
      name: "Patron Experience",
      description: "Enhance the experience for club-goers",
      features: [
        {
          title: "Queue Skipping",
          description: "App-based 'fast pass' or timed entry reservations to avoid long lines",
          status: "Coming Soon"
        },
        {
          title: "Mobile Ordering",
          description: "Order drinks/food from your phone to skip the bar queue",
          status: "In Development"
        },
        {
          title: "Smart Recommendations",
          description: "Personalized event and drink suggestions based on preferences",
          status: "Planned"
        },
        {
          title: "Friend Finder",
          description: "Opt-in location sharing with friends inside the venue",
          status: "Planned"
        },
        {
          title: "Loyalty Rewards",
          description: "Earn points for attending events, buying drinks, or bringing friends",
          status: "Coming Soon"
        },
        {
          title: "Rideshare Integration",
          description: "In-app partnership with Uber, Lyft, or taxis for safe rides home",
          status: "In Development"
        }
      ]
    },
    {
      id: "security",
      name: "Security & Safety",
      description: "Advanced tools for security staff and patron safety",
      features: [
        {
          title: "Digital ID Verification",
          description: "Scan IDs with the app for quick age and identity verification",
          status: "Coming Soon"
        },
        {
          title: "Capacity Management",
          description: "Real-time headcounts and alerts when capacity thresholds are reached",
          status: "In Development"
        },
        {
          title: "Incident Reporting",
          description: "Quick logging and documentation of security incidents",
          status: "Planned"
        },
        {
          title: "Security Communication",
          description: "Instant staff chat and location sharing for coordinated response",
          status: "Coming Soon"
        },
        {
          title: "Guest Safety",
          description: "Find My Friend features and discreet SOS function for patron safety",
          status: "In Development"
        },
        {
          title: "Emergency Protocols",
          description: "Built-in steps for handling emergencies with quick reference instructions",
          status: "Planned"
        }
      ]
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Coming Soon":
        return "bg-purple-500/20 text-purple-300 border-purple-500/30";
      case "In Development":
        return "bg-blue-500/20 text-blue-300 border-blue-500/30";
      case "Planned":
        return "bg-gray-500/20 text-gray-300 border-gray-500/30";
      default:
        return "bg-gray-500/20 text-gray-300 border-gray-500/30";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-gray-800">
      <div className="fixed top-0 left-0 right-0 z-50 flex justify-between items-center px-4 py-4">
        <NocheLogo />
        <BusinessMenu />
      </div>
      
      <div className="max-w-7xl mx-auto pt-28 pb-20 px-6">
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            size="sm"
            className="text-gray-400 hover:text-white"
            asChild
          >
            <Link to="/">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Link>
          </Button>
        </div>
        
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-white mb-4">Feature Showcase</h1>
          <p className="text-gray-400 max-w-3xl">
            Explore the current and upcoming features of NocheNoche. This showcase demonstrates 
            the platform's capabilities for nightclub owners, promoters, staff, and patrons.
          </p>
        </div>
        
        <Tabs defaultValue="guest-management" className="w-full">
          <div className="mb-8 overflow-x-auto no-scrollbar">
            <TabsList className="bg-gray-900/50 p-1 mb-4 flex flex-nowrap min-w-max">
              {featureCategories.map((category) => (
                <TabsTrigger
                  key={category.id}
                  value={category.id}
                  className="px-4 py-2 whitespace-nowrap"
                >
                  {category.name}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          
          {featureCategories.map((category) => (
            <TabsContent key={category.id} value={category.id} className="mt-0">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {category.features.map((feature, index) => (
                  <Card 
                    key={index} 
                    className="bg-gray-900/50 border border-gray-800 hover:border-gray-700 transition-all"
                  >
                    <CardHeader>
                      <div className="flex justify-between items-start mb-2">
                        <CardTitle className="text-xl">{feature.title}</CardTitle>
                        <span className={`px-3 py-1 text-xs font-semibold rounded-full border ${getStatusColor(feature.status)}`}>
                          {feature.status}
                        </span>
                      </div>
                      <CardDescription>{feature.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Button 
                        variant="secondary" 
                        className="w-full bg-gray-800 hover:bg-gray-700"
                        onClick={() => {}}
                      >
                        Preview
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
              
              <div className="mt-12 text-center">
                <p className="text-gray-400 mb-4">Want to see this feature in action?</p>
                <Button className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700">
                  Request Demo
                </Button>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
};

export default FeatureShowcase;
